/**
 * Circular byte-bounded buffer.
 *
 * - O(1) append
 * - O(1) drop oldest
 * - No Array.shift()
 * - Maintains exact byte cap
 */
class CircularTextBuffer {
    private buffer: (string | undefined)[];
    // head - points to oldest element
    private head = 0;
    // tail - points to next write position
    private tail = 0;
    // size - number of elements currently stored
    private size = 0;
    // totalBytes - total bytes currently stored
    private totalBytes = 0;

    private readonly capacity = 10_000;
    private readonly maxBytes = 5_000_000;

    constructor() {
        this.buffer = new Array(this.capacity);
    }

    /**
     * Push a new block.
     * Drops oldest blocks until maxBytes constraint satisfied.
     *
     * Returns number of dropped blocks.
     */
    push(value: string): number {
        let valueToPush = value;
        let dropped = 0;
        const valueLength = value.length;

        // If single block exceeds maxBytes, truncate it hard.
        if (valueLength > this.maxBytes) {
            valueToPush = value.slice(valueLength - this.maxBytes);
        }

        // If buffer is full by element count, drop oldest.
        if (this.size === this.capacity) {
            dropped += this.dropOldest();
        }

        // Insert at tail
        this.buffer[this.tail] = valueToPush;
        this.tail = (this.tail + 1) % this.capacity;
        this.size++;
        this.totalBytes += valueToPush.length;

        // Enforce byte constraint
        while (this.totalBytes > this.maxBytes && this.size > 0) {
            dropped += this.dropOldest();
        }

        return dropped;
    }

    clear() {
        this.buffer = new Array(this.capacity);
        this.head = 0;
        this.tail = 0;
        this.size = 0;
        this.totalBytes = 0;
    }

    private dropOldest(): number {
        const oldest = this.buffer[this.head];
        if (oldest !== undefined) {
            this.totalBytes -= oldest.length;
        }

        this.buffer[this.head] = undefined;
        this.head = (this.head + 1) % this.capacity;
        this.size--;

        return 1;
    }

    /**
     * Iterate in logical order (oldest → newest)
     */
    forEach(callback: (value: string) => void) {
        for (let i = 0; i < this.size; i++) {
            const index = (this.head + i) % this.capacity;
            const value = this.buffer[index];
            if (value !== undefined) {
                callback(value);
            }
        }
    }

    /**
     * Iterate in logical order from startIndex (inclusive) to end.
     * Use when only new blocks need to be processed.
     */
    forEachFrom(startIndex: number, callback: (value: string) => void) {
        for (let i = startIndex; i < this.size; i++) {
            const index = (this.head + i) % this.capacity;
            const value = this.buffer[index];
            if (value !== undefined) {
                callback(value);
            }
        }
    }

    /**
     * Iterate at most maxCount blocks from startIndex.
     * Returns number of blocks actually processed.
     */
    forEachFromWithLimit(startIndex: number, maxCount: number, callback: (value: string) => void): number {
        let count = 0;
        for (let i = startIndex; i < this.size && count < maxCount; i++) {
            const index = (this.head + i) % this.capacity;
            const value = this.buffer[index];
            if (value !== undefined) {
                callback(value);
                count++;
            }
        }
        return count;
    }

    getSize() {
        return this.size;
    }
}

export default CircularTextBuffer;
